import { forwardRef } from "react";
import { Input } from "@/components/ui/input";

/**
 * NumberInput — text input with Indonesian thousand separator (dot).
 * Stores raw numeric value; displays formatted (e.g., 6000000000 → "6.000.000.000").
 *
 * Props:
 *  - value: number | string (raw)
 *  - onChange(rawNumberString): callback with digits-only string
 *  - allowDecimal (optional): allow one comma as decimal separator
 */
const NumberInput = forwardRef(function NumberInput(
  { value, onChange, allowDecimal = false, ...rest },
  ref,
) {
  const raw = value === null || value === undefined || value === "" ? "" : String(value);
  const display = formatId(raw, allowDecimal);

  const handleChange = (e) => {
    let next = e.target.value;
    if (allowDecimal) {
      // keep digits + at most one comma
      next = next.replace(/[^0-9,]/g, "");
      const parts = next.split(",");
      if (parts.length > 2) next = parts[0] + "," + parts.slice(1).join("");
    } else {
      next = next.replace(/\D/g, "");
    }
    // strip leading zeros (but keep single "0")
    next = next.replace(/^0+(?=\d)/, "");
    onChange?.(next);
  };

  return (
    <Input
      ref={ref}
      type="text"
      inputMode="numeric"
      value={display}
      onChange={handleChange}
      {...rest}
    />
  );
});

function formatId(raw, allowDecimal) {
  if (raw === "" || raw == null) return "";
  if (allowDecimal) {
    const [intPart, decPart] = String(raw).split(",");
    const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return decPart != null ? `${grouped},${decPart}` : grouped;
  }
  return String(raw).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

export default NumberInput;
